<script setup lang="ts">
import { transformImage } from '@/utils';
import { getBeUrl } from '@/utils/url';
import type { PropType } from 'vue';
import type { Template } from '~/api/interface';
import Resource from '~/components/resource';
import { getTagIDs } from '~/features/template';
import { useTemplateDetail } from '~/hooks/useModal/modals/template';
import style from './media.module.scss';
import Play from './play.vue';

const props = defineProps({
  template: {
    type: Object as PropType<Template>,
    required: true,
  },
  type: {
    type: String,
    default: 'vertical',
  },
});

const emit = defineEmits(['reload']);

const resourceRef = ref();
const isPlaying = ref(false);
const posterUsed = ref(false);

function onPlay() {
  isPlaying.value = true;
  posterUsed.value = true;
}

function onPause() {
  isPlaying.value = false;
}

function reload() {
  emit('reload');
}

const resourceLocation = injectResourceLocation();
const userInfo = useUserInfo();

function showTemplateDetail() {
  trackEvent(TRACK_EVENT.CLICK_TEMPLATE_PREVIEW, {
    template_id: props.template.id,
    template_title: props.template.name,
    source: resourceLocation?.source,
    tags: getTagIDs(props.template.tag_ids).map(item => item.tag_name),
    org_id: userInfo.value?.organization_id,
  });

  useTemplateDetail([], props.template, { source: resourceLocation?.source }, reload);
}

const url = computed(() => {
  return props.template.preview_video || props.template.hover_video;
});

const poster = computed(() => {
  const isVertical = props.type === 'vertical';
  let posterUrl = props.template.cover_url || props.template.cover_oss_path;
  if (!posterUrl.startsWith('http')) {
    posterUrl = getBeUrl(`/${posterUrl}`);
  }
  return transformImage(
    posterUrl,
    isVertical
      ? {
          width: 1080,
          height: 1920,
        }
      : {
          width: 1920,
          height: 1080,
        },
  );
});
</script>

<template>
  <div
    v-if="type === 'vertical'"
    class="hover-video-container relative vertical w-[226px] h-[402px]"
  >
    <slot name="before" />
    <Resource
      ref="resourceRef"
      :src="url"
      :class="style.verticalResource"
      loop
      muted
      :lazy="true"
      :poster="poster"
      @play="onPlay"
      @pause="onPause"
      @mouseover="resourceRef?.play()"
      @mouseout="resourceRef?.pause()"
      @click="showTemplateDetail"
    />
    <slot name="overlay" :is-playing="isPlaying" :vertical="true">
      <Play v-if="!isPlaying" :class="[style.videoAction, 'ml-[14px]']" />
    </slot>
    <slot name="after" />
  </div>

  <div v-else class="hover-video-container relative horizontal w-[600px] h-[337px]">
    <slot name="before" />
    <Resource
      ref="resourceRef"
      :class="style.normalResource"
      :src="url"
      loop
      muted
      :lazy="true"
      :poster="poster"
      @play="onPlay"
      @pause="onPause"
      @mouseover="resourceRef?.play()"
      @mouseout="resourceRef?.pause()"
      @click="showTemplateDetail"
    />
    <slot name="overlay" :is-playing="isPlaying" :vertical="false">
      <Play v-if="!isPlaying" :class="style.videoAction" />
    </slot>
    <slot name="after" />
  </div>
</template>
